import { use_effect } from '@/hooks/_base/use_effect';
import { path_relative_to_absolute } from '@/utils/utils_files_client';
import { assert_string_not_empty, assert_true } from '@shared/asserts/utils_asserts';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { DTO__image_asset } from '@shared/dto/+image/image_asset_base/DTO__image_asset';
import { DTO_GRAPH__instance } from '@shared/dto/generation/graph_invoke/_graph/DTO_GRAPH__instance';
import { FN_SERIALIZATION__data_deserialize } from '@shared/serialization/FN_SERIALIZATION__data_deserialize';
import { UTILS__fflate } from '@shared/utils/UTILS__fflate';
import { logw } from '@shared/utils/utils_logs';
import ExifReader from 'exifreader';
import { useState } from 'react';

export function HOOK_IMAGE__get_graph_source(image: DTO__image_asset) {
  const [graph_source, set_graph_source] = useState(DTO_GRAPH__instance.CREATE_STUB());

  use_effect(() => {
    if (image.is_stub()) return;

    assert_string_not_empty(image.url_relative);

    let is_cancelled = false;

    try {
      void try_load_source_from_xmp(is_cancelled);
    } catch (error) {
      logw(`cant load graph source from image\t\t${image.url_relative}`);
    }

    return () => {
      is_cancelled = true;
    };
  }, [image]);

  /*
   * Loads and extracts the graph source schema from the image's XMP metadata.
   */
  async function try_load_source_from_xmp(is_cancelled: boolean) {
    let url_absolute = path_relative_to_absolute(image.url_relative);

    // 1. Fetch the image file as an ArrayBuffer using the relative URL
    const response = await fetch(url_absolute);
    assert_true(response.ok);

    const file_buffer = await response.arrayBuffer();

    if (is_cancelled) return;

    // 2. Use ExifReader to parse the metadata tags
    const tags = ExifReader.load(file_buffer);

    // 3. Extract the target key from the metadata hooks
    const target_key = CONSTANTS__shared.IMAGE_METADATA_KEY_JSON_GRAPH;
    const json_graph_compressed = tags[target_key];

    if (!json_graph_compressed) return;
    if (!json_graph_compressed.description) return;

    // ExifReader usually stores custom XMP text fields inside the 'description' property
    const base64_compressed_string = json_graph_compressed.description.trim();

    // 4. Decompress the base64 string using your fflate utility
    const decompressed_json_string = UTILS__fflate.decompress_string(base64_compressed_string);

    // 5. Parse the JSON back into an object
    const graph_data_raw = JSON.parse(decompressed_json_string);

    const graph = FN_SERIALIZATION__data_deserialize(graph_data_raw);

    // 6. Update the state if the hook context hasn't shifted to a new image
    // If your DTO requires an instantiation method (e.g., FROM_JSON), wrap it here.
    // Otherwise, pass the object directly or cast it if it's a plain object interface.
    set_graph_source(graph);
  }

  return { graph_source };
}
